import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from 'lucide-react';

import type { Environment } from '@/api/apiTypes';
import { InputForm } from '@/components/CustomForm/components';
import { AppModal } from '@/components/Modal/AppModal';
import { cn } from '@/utils/cn';
import '@/components/users/AdminInviteForm.scss';

type InviteFormValues = {
  email: string;
};

type AdminInviteFormProps = {
  open: boolean;
  environments: Environment[];
  submitting: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    values: { 
      email: string; 
      environment_ids?: number[] 
    }
  ) => Promise<void>;
};

export function AdminInviteForm({
  open,
  environments,
  submitting,
  onOpenChange,
  onSubmit,
}: AdminInviteFormProps) {
  const { t } = useTranslation();
  const [selectedEnvironmentIds, setSelectedEnvironmentIds] = useState<number[]>([]);
  const [environmentsMenuOpen, setEnvironmentsMenuOpen] = useState(false);
  const environmentsSelectRef = useRef<HTMLDivElement | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormValues>({
    defaultValues: {
      email: '',
    },
  });

  const clearFormState = () => {
    reset({ email: '' });
    setSelectedEnvironmentIds([]);
    setEnvironmentsMenuOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      clearFormState();
    }

    onOpenChange(nextOpen);
  };

  useEffect(() => {
    if (!environmentsMenuOpen) {
      return undefined;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      const clickTarget = event.target as Node;

      if (!environmentsSelectRef.current?.contains(clickTarget)) {
        setEnvironmentsMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setEnvironmentsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [environmentsMenuOpen]);

  const toggleEnvironment = (environmentId: number) => {
    setSelectedEnvironmentIds((currentSelection) =>
      currentSelection.includes(environmentId)
        ? currentSelection.filter((value) => value !== environmentId)
        : [...currentSelection, environmentId],
    );
  };

  const removeEnvironment = (environmentId: number) => {
    setSelectedEnvironmentIds((currentSelection) => currentSelection.filter((value) => value !== environmentId));
  };

  const selectedEnvironmentItems = useMemo(
    () => environments.filter((environment) => selectedEnvironmentIds.includes(environment.id)),
    [environments, selectedEnvironmentIds],
  );

  const environmentsTriggerText = useMemo(() => {
    if (selectedEnvironmentItems.length === 0) {
      return t('users.form.environmentsPlaceholder');
    }

    if (selectedEnvironmentItems.length === 1) {
      return selectedEnvironmentItems[0].name;
    }

    return `${selectedEnvironmentItems.length} ${t('users.form.environmentsSelectedSuffix')}`;
  }, [selectedEnvironmentItems, t]);

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (submitting) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setEnvironmentsMenuOpen((openValue) => !openValue);
    }
  };

  return (
    <AppModal
      open={open}
      onOpenChange={handleOpenChange}
      title={t('users.form.createTitle')}
      description={t('users.form.createDescription')}
      size="wide"
      footer={
        <div className="form-actions">
          <button
            type="button"
            className="button button--secondary"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            {t('users.actions.cancel')}
          </button>
          <button 
            type="submit" 
            form="admin-invite-form" 
            className="button button--primary" 
            disabled={submitting}
          >
            {submitting 
              ? t('users.actions.sendingInvite') 
              : t('users.actions.sendInvite')
            }
          </button>
        </div>
      }
    >
      <form
        id="admin-invite-form"
        className="stack"
        onSubmit={handleSubmit(async (values) => {
          await onSubmit({
            email: values.email,
            environment_ids: selectedEnvironmentIds.length 
              ? selectedEnvironmentIds 
              : undefined,
          });
          clearFormState();
        })}
      >
        <InputForm
          label={t('users.columns.email')}
          type="email"
          placeholder={t('users.form.emailPlaceholder')}
          autoComplete="email"
          error={errors.email?.message}
          {...register('email', {
            required: t('users.validation.emailRequired'),
          })}
        />

        <div className="stack">
          <div>
            <h3>{t('users.form.environmentsTitle')}</h3>
            <p className="section-copy">{t('users.form.environmentsDescription')}</p>
          </div>

          {environments.length ? (
            <div className="invite-environment-select" ref={environmentsSelectRef}>
              <div
                className={cn(
                  'field__control invite-environment-select__trigger',
                  selectedEnvironmentItems.length === 0 && 'invite-environment-select__trigger--placeholder',
                )}
                onClick={() => {
                  if (submitting) {
                    return;
                  }

                  setEnvironmentsMenuOpen((openValue) => !openValue);
                }}
                onKeyDown={handleTriggerKeyDown}
                aria-haspopup="listbox"
                aria-expanded={environmentsMenuOpen}
                aria-label={environmentsTriggerText}
                role="button"
                aria-disabled={submitting}
                tabIndex={submitting ? -1 : 0}
              >
                {selectedEnvironmentItems.length > 0 ? (
                  <div className="invite-environment-select__trigger-scroll" aria-label={t('users.form.environmentsSelectedAriaLabel')}>
                    {selectedEnvironmentItems.map((environment) => (
                      <span key={environment.id} className="invite-environment-select__chip">
                        <span className="invite-environment-select__chip-label">{environment.name}</span>
                        <button
                          type="button"
                          className="invite-environment-select__chip-remove"
                          aria-label={t('users.form.removeEnvironment', { name: environment.name })}
                          onClick={(event) => {
                            event.stopPropagation();
                            removeEnvironment(environment.id);
                          }}
                          disabled={submitting}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="invite-environment-select__trigger-value">{environmentsTriggerText}</span>
                )}

                <ChevronDown
                  size={16}
                  className={cn(
                    'invite-environment-select__trigger-icon',
                    environmentsMenuOpen && 'invite-environment-select__trigger-icon--open',
                  )}
                />
              </div>

              {environmentsMenuOpen ? (
                <div className="invite-environment-select__menu" role="listbox" aria-multiselectable="true">
                  {environments.map((environment) => (
                    <label key={environment.id} className="invite-environment-select__option">
                      <input
                        type="checkbox"
                        checked={selectedEnvironmentIds.includes(environment.id)}
                        onChange={() => toggleEnvironment(environment.id)}
                        disabled={submitting}
                      />
                      <span>{environment.name}</span>
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <p className="section-copy">{t('users.form.noEnvironments')}</p>
          )}
        </div>
      </form>
    </AppModal>
  );
}
